/**
 * DotMapBus — cross-instance event bus singleton.
 *
 * Lives on `window.dotMap`. All <dot-map-*> instances on a page share it.
 * Hosts (Webflow page, parent app) fire events through it; instances subscribe.
 *
 *   window.dotMap.fire('member-joined', { region, role, pueblo });
 *   window.dotMap.on('member-joined', (detail) => { ... });
 *
 * v3 architecture §1: optional BroadcastChannel for cross-tab sync via the
 * `cross-tab="true"` attribute on a <dot-map-*> element (disabled in v1, the
 * scaffold ships ready for opt-in).
 *
 * Per stu_plan--epl-dot-map-component-v3.md.
 */

export type DotMapEventName =
  | 'member-joined'      // host fires when a new signer lands → instances ripple
  | 'role-filter'        // host fires to set active role filter
  | 'fire-ripple'        // host fires to trigger arbitrary ripple at coords
  | 'reset'              // clear all state across instances
  | string;              // host can fire arbitrary custom events too

export interface DotMapBusEventDetail {
  [key: string]: unknown;
}

export class DotMapBus extends EventTarget {
  private _broadcastChannel: BroadcastChannel | null = null;

  /** Fire an event to all listeners (and other tabs if cross-tab enabled). */
  fire(name: DotMapEventName, detail: DotMapBusEventDetail = {}): void {
    this.dispatchEvent(new CustomEvent(name, { detail }));
    this._broadcastChannel?.postMessage({ name, detail });
  }

  /** Subscribe to an event. Returns unsubscribe fn. */
  on(name: DotMapEventName, handler: (detail: DotMapBusEventDetail) => void): () => void {
    const wrapped = (e: Event) => handler((e as CustomEvent).detail);
    this.addEventListener(name, wrapped);
    return () => this.removeEventListener(name, wrapped);
  }

  /** Enable cross-tab sync via BroadcastChannel. Idempotent. */
  enableCrossTab(channelName = 'dot-map'): void {
    if (this._broadcastChannel) return;
    if (typeof BroadcastChannel === 'undefined') return;
    this._broadcastChannel = new BroadcastChannel(channelName);
    this._broadcastChannel.onmessage = (e) => {
      const { name, detail } = e.data ?? {};
      if (typeof name === 'string') {
        // Re-dispatch locally without re-broadcasting (avoid loops)
        this.dispatchEvent(new CustomEvent(name, { detail }));
      }
    };
  }

  disableCrossTab(): void {
    this._broadcastChannel?.close();
    this._broadcastChannel = null;
  }
}

declare global {
  interface Window {
    dotMap: DotMapBus;
  }
}

/** Singleton accessor — installs `window.dotMap` lazily on first call. */
export function getDotMapBus(): DotMapBus {
  if (typeof window === 'undefined') {
    // SSR safety — return a throwaway instance during build/prerender
    return new DotMapBus();
  }
  if (!window.dotMap) {
    window.dotMap = new DotMapBus();
  }
  return window.dotMap;
}
